// Shared rules for tenant-managed type catalogues (work-order types, placement sub-types,
// checklists): a row is identified by its id and an immutable `code` generated once from the
// name; `name` is only the display label. Dependency-free on purpose: the browser apps import
// this file directly (portal via `@phrospr/lib-core/type-catalogue`), so it must never pull
// the rest of the barrel (moment, decimal.js) along.

export const TYPE_NAME_MAX_LENGTH = 60;
const TYPE_CODE_MAX_LENGTH = 50;
const FALLBACK_CODE = 'type';

export interface ITypeCatalogueRow {
    active: boolean;
    // Whether any record references the row; decides delete vs deactivate and whether an
    // inactive row still earns a place in list filters.
    in_use?: boolean;
}

export function normalizeTypeName(name: unknown): string | null {
    if (typeof name !== 'string') return null;
    const trimmed = name.trim().replace(/\s+/g, ' ');
    if (!trimmed.length || trimmed.length > TYPE_NAME_MAX_LENGTH) return null;
    return trimmed;
}

export function isTypeNameTaken(name: string, existingNames: string[]): boolean {
    const wanted = name.trim().toLowerCase();
    return existingNames.some(existing => existing.trim().toLowerCase() === wanted);
}

export function slugifyTypeCode(name: string, existingCodes: string[]): string {
    const base =
        name
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .slice(0, TYPE_CODE_MAX_LENGTH)
            .replace(/_+$/g, '') || FALLBACK_CODE;
    const taken = new Set(existingCodes);
    if (!taken.has(base)) return base;
    let suffix = 2;
    while (taken.has(`${base}_${suffix}`)) suffix++;
    return `${base}_${suffix}`;
}

// A reorder must name every existing row exactly once: a partial list would leave the
// remaining rows at sequences that collide with the new ones.
export function isValidReorder(orderedIds: unknown, existingIds: number[]): orderedIds is number[] {
    if (!Array.isArray(orderedIds) || orderedIds.length !== existingIds.length) return false;
    const wanted = new Set(orderedIds);
    if (wanted.size !== orderedIds.length) return false;
    return existingIds.every(id => wanted.has(id));
}

// Inactive types can no longer be chosen, but a record that already carries one keeps it.
export function isTypeSelectable(type: ITypeCatalogueRow | null | undefined, isCurrentValue: boolean): boolean {
    if (!type) return false;
    return type.active || isCurrentValue;
}

// Create/edit dropdowns: every active row, plus the one the record already carries.
export function selectableTypes<T extends ITypeCatalogueRow>(
    types: T[],
    getId: (type: T) => number,
    currentId: number | null | undefined,
): T[] {
    return types.filter(type => isTypeSelectable(type, getId(type) === currentId));
}

// List filters keep inactive rows findable without adding noise: an inactive row only earns a
// place when records still reference it, or when it is part of the current selection (a saved
// default filter must stay visible so it can be unticked).
export function splitTypesForFilter<T extends ITypeCatalogueRow>(
    types: T[],
    getId: (type: T) => number,
    selectedIds: number[],
): { active: T[]; inactive: T[] } {
    const selected = new Set(selectedIds);
    return {
        active: types.filter(type => type.active),
        inactive: types.filter(type => !type.active && (type.in_use || selected.has(getId(type)))),
    };
}
